import { P2Alert, P2TextDescription, P2TextLabel } from 'react-bootstrap-common-script';
import { Separator } from '../separator';
import FinancialPlanStatus from './financial-plan-status/financial-plan-status';
import './current-owners-financial-plan-section.scss';
import { useAppSelector } from '../../redux/hook';
import { useMemo } from 'react';
import { StructureStatusEnum } from '../../models/enum/structure-status.enum';
import { StageDCurrentFinancialPlan, StageDProposedFinancialPlan } from '../../models/classes/stage-d/Review-Application.class';
import { Helpers } from '../../utils/helper';

interface CurrentProposedOwnerFinancialPlanSectionInterface {
  ownerType: "current" | "proposed";
  isPreliminary: boolean;
  infoboxStatus: "info" | "success";
  infoboxTitle: string;
  infoboxText: string;
  currentFinancialPlanData?: StageDCurrentFinancialPlan;
  proposedFinancialPlanData?: StageDProposedFinancialPlan;
}

const CurrentProposedOwnerFinancialPlanSection = ({ isPreliminary = false, ...props }: CurrentProposedOwnerFinancialPlanSectionInterface) => {
  const applicationStep1 = useAppSelector(state => state.app.ApplicationStep1Slice.formData);

  const currentOwners = useMemo(() => {
    return applicationStep1.household.owners.map(e => e.myinfo.name );
  }, [applicationStep1.household.owners]);

  const proposedOwners = useMemo(() => {
    return applicationStep1.household.incoming_owners.map(e => e.myinfo.name );
  }, [applicationStep1.household.incoming_owners])

  const outgoingOwners = useMemo(() => {
    return applicationStep1.household.owners.filter((owner) => owner.structure_status === StructureStatusEnum.outgoing);
  }, [applicationStep1.household.owners]);

  let completionAppointmentDate = "";
  let preparedDate = "";
  let preparedTime = "";
  if (props.ownerType === "current") {
    completionAppointmentDate = props.currentFinancialPlanData?.actual_completion_date === undefined
      ? props.currentFinancialPlanData?.scheduled_completion_date === undefined
        ? props.currentFinancialPlanData?.tentative_completion_date!
        : props.currentFinancialPlanData?.scheduled_completion_date
      : props.currentFinancialPlanData?.actual_completion_date;
    
    preparedDate = Helpers.formatDate(props.currentFinancialPlanData?.prepared_on, "short");
    preparedTime = Helpers.formatTime(props.currentFinancialPlanData?.prepared_on.split("T")[1]);
  } else {
    completionAppointmentDate = props.proposedFinancialPlanData?.actual_completion_date === undefined
      ? props.proposedFinancialPlanData?.scheduled_completion_date === undefined
        ? props.proposedFinancialPlanData?.tentative_completion_date!
        : props.proposedFinancialPlanData?.scheduled_completion_date
      : props.proposedFinancialPlanData?.actual_completion_date;
  
    preparedDate = Helpers.formatDate(props.proposedFinancialPlanData?.prepared_on, "short");
    preparedTime = Helpers.formatTime(props.proposedFinancialPlanData?.prepared_on.split("T")[1]);
  }
  completionAppointmentDate = Helpers.formatDate(completionAppointmentDate, "short");

  return (
    <div>
      <div className='polymer-current-owner-financial-plan-top-section-container'>
        <div className='polymer-current-owner-financial-plan-top-section-container__title-container'>
          <div className='polymer-current-owner-financial-plan-top-section-container__title-container-title'>
            <P2TextDescription color="#000" title={props.ownerType === "current" ? "Current Owners' Financial Plan" : "Proposed Owners' Financial Plan"} />
            {isPreliminary && <FinancialPlanStatus content="Preliminary" status="pending" />}
          </div>
          <div>
            <i>Prepared on {preparedDate} at {preparedTime}</i>
          </div>
          <p className="polymer-current-owner-financial-plan-top-section-container__title-container-desc">
            The financial plan details the finances required for the completion of the change in flat ownership. Please review your financial plan which is
            calculated based on the appointment date.
          </p>  
        </div>
        <div className='polymer-current-owner-financial-plan-top-section-container__cpf-data-retrieval'>
          <P2TextLabel fontSize={"1.125rem"} bold={700} label={"Completion Appointment Date"} />
          <P2TextLabel fontSize={"1.125rem"} bold={400} label={completionAppointmentDate} />
        </div>
        <div className={props.infoboxStatus === 'info' ? 'polymer-current-owner-financial-plan-top-section-container__info-box' : '' }>
          <P2Alert 
            withBorder
            variant={props.infoboxStatus}
            title={props.infoboxTitle}
            text={props.infoboxText}
          />
        </div>
        <div className='polymer-current-owner-financial-plan-top-section-container__current-owner'>
          <P2TextLabel fontSize={"1.125rem"} bold={700} label={props.ownerType === "current" ? "Current Owner(s):" : "Proposed Owner(s):"} />
          {props.ownerType === "current" ? currentOwners.map((owner) => {
            return (
              <P2TextLabel fontSize={"1.125rem"} bold={400} label={owner} />
            )
          }) : proposedOwners.map((owner) => {
            return (
              <P2TextLabel fontSize={"1.125rem"} bold={400} label={owner} />
            )
          })}
        </div>
        {props.ownerType === "current" &&
          <div className='polymer-current-owner-financial-plan-top-section-container__outstanding-morgage-loan'>
            <P2TextLabel fontSize={"1.125rem"} bold={700} label={"Outstanding Mortgage Loan"} />
            <P2TextLabel fontSize={"1.125rem"} bold={400} label={Helpers.amountToTwoDecimals(props.currentFinancialPlanData?.outstanding_mortgage_loan)} />
          </div>
        }
        {props.ownerType === "current" && outgoingOwners.length > 0 &&
          <div className='polymer-current-owner-financial-plan-top-section-container__cpf-refund'>
            <div className='polymer-current-owner-financial-plan-top-section-container__cpf-refund-primary-row'>
              <P2TextLabel fontSize={"1.25rem"} bold={700} label={"CPF Refund to Outgoing Owners"} />
              <P2TextLabel fontSize={"1.25rem"} bold={700} label={Helpers.amountToTwoDecimals(props.currentFinancialPlanData?.total_cpf_refund_to_outgoing)} />
            </div>
            <div className='polymer-current-owner-financial-plan-top-section-container__cpf-refund-separator'>
              <Separator variant='solid' />
            </div>
            <div className='polymer-current-owner-financial-plan-top-section-container__cpf-refund-non-primary-row-container'>
              {props.currentFinancialPlanData?.cpf_refund_co.map((cpfRefundCo) => {
                return (
                  <div className='polymer-current-owner-financial-plan-top-section-container__cpf-refund-non-primary-row'>
                    <P2TextLabel fontSize={"1.125rem"} bold={700} label={cpfRefundCo.name} />
                    <P2TextLabel fontSize={"1.125rem"} bold={700} label={Helpers.amountToTwoDecimals(cpfRefundCo.amount)} />
                  </div>
                )
              })}
            </div>
          </div>
        }
      </div>
    </div>
  )
}

export default CurrentProposedOwnerFinancialPlanSection